// @ts-check
/**
 * #1957 — de regels waarmee de vondsten van PeckArchive tot voorstellen worden: welke startpunten een
 * familievraag krijgen, wanneer twee akten dezelfde voorouder noemen (samenvoegen) en wanneer niet
 * (tegenspraak), en welk niveau een voorstel krijgt (het zwakste). Gedeeld door de betaalde laag
 * (src/components/person-editor/vondsten) en de gratis preview (routes/archives.js): de preview mag
 * nooit een kaart tonen die de betaalde laag tegenspreekt (eis PeckArchive, 02-11).
 */
import { zelfdeKlank, zelfdeNaam, zelfdePersoonsnaam } from './naamGelijkenis.mjs';

// ---------------------------------------------------------------------------------------------
// Namen
// ---------------------------------------------------------------------------------------------

export const TUSSENVOEGSELS = [
  'van de',
  'van der',
  'van den',
  'van het',
  'van',
  'de',
  'den',
  'der',
  'het',
  "'t",
  'te',
  'ter',
  'ten',
  'bij',
  'op',
  'in',
  'uit',
  'onder',
  'over',
];
const TUSSEN = new Set(TUSSENVOEGSELS.flatMap((t) => t.split(' ')));

/**
 * Akte-ID's zijn GUID's: één vorm (klein, zonder accolades) als `normaliseerAkteId` op de server.
 * @param {string} id
 */
// Bewust zonder String(): een misvormd PeckArchive-item (akte_id geen tekst) gooit, en de aanroeper vangt
// dat (useOokGevonden: het blad werkt dan zoals zonder "Ook gevonden"; de preview slaat het eindpunt over).
export const akteSleutel = (id) => id.replace(/[{}]/g, '').trim().toLowerCase();

/** @param {{ akte_id: string, pid: string }} s */
export const stapSleutel = (s) => `${akteSleutel(s.akte_id)}|${s.pid}`;

/**
 * PA schrijft namen klein ("anna maria", "van der ham"): hoofdletter per woord, tussenvoegsels klein.
 * @param {...string} delen
 */
export const naamUitPa = (...delen) =>
  delen
    .join(' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) =>
      i > 0 && TUSSEN.has(w)
        ? w
        : w.replace(
            /(^|-)(\p{L})/gu,
            (_, /** @type {string} */ v, /** @type {string} */ c) => v + c.toUpperCase()
          )
    )
    .join(' ');

/**
 * @typedef {{ voornaam: string | null, tussenvoegsel: string | null, achternaam: string | null,
 *   patroniem: string | null }} NaamWeergave
 */

/**
 * De naam van PeckArchive (naam_weergave) als één regel: voornaam, patroniem, tussenvoegsel, achternaam.
 * @param {NaamWeergave} nw
 */
export const weergaveNaam = (nw) =>
  [nw.voornaam, nw.patroniem, nw.tussenvoegsel, nw.achternaam].filter(Boolean).join(' ');

/**
 * De naam zoals de akte hem noemt; zonder `naam_weergave` de genormaliseerde vorm met hoofdletters.
 * @param {{ naam: { voornaam: string, achternaam: string }, naam_weergave?: NaamWeergave | null }} x
 */
export const toonNaam = (x) =>
  (x.naam_weergave && weergaveNaam(x.naam_weergave)) ||
  naamUitPa(x.naam.voornaam, x.naam.achternaam);

/**
 * Een naam in één regel splitsen: de achternaam is het laatste woord met de tussenvoegsels ervoor.
 * @param {string} n
 */
const splits = (n) => {
  const w = n.trim().split(/\s+/);
  let i = w.length - 1;
  while (i > 0 && TUSSEN.has(w[i - 1].toLowerCase())) i--;
  i = Math.max(i, 0);
  return { voornaam: w.slice(0, i).join(' '), achternaam: w.slice(i).join(' ') };
};
/**
 * Dezelfde naam voor "bevestigt", "samenvoegen" en "naamvariant": de gedeelde strenge regel
 * (`zelfdePersoonsnaam`, zie naamGelijkenis.mjs). Wouter en Waltherus blijven twee namen, en
 * Woudstra is niet Woudenbergh: een mogelijke variant is aan de genealoog, niet aan ons.
 * @param {string} a
 * @param {string} b
 */
export const zelfdeNaamTekst = (a, b) => zelfdePersoonsnaam(splits(a), splits(b));

/**
 * #1964 (Emma 03-11) — "N.N." (of onbekend, ?), ook als voornaam ("N.N. Kool"): de akte noemt deze ouder
 * niet bij naam. Geen naam, dus geen tegenspraak en geen voorstel.
 * @param {string} naam
 */
export const geenNaam = (naam) =>
  /^(n\.?\s?n\.?|nn|onbekend|unknown|\?+)(\s|$)/i.test(naam.trim()) || !/\p{L}{2}/u.test(naam);

/**
 * #1964 (Emma 03-11) — de ouders van deze persoon zelf (generatie 1, dezelfde plek): de akten zijn zijn
 * eigen doop, huwelijk en overlijden, en een kind heeft één vader en één moeder. Daar is een spellingsvariant
 * met dezelfde voornaam geen tegenspraak (Kraijesteijn, Kraijestijn, Kraaijenstein): `zelfdeNaam` (voornaam
 * gelijk, achternaam die erop lijkt) in plaats van de strenge klankregel. Een andere voornaam (Fredrik /
 * Frank) of een andere achternaam (Kraijesteijn / Verbree) blijft tegenspraak.
 * @param {string} a
 * @param {string} b
 * @param {string} pad
 */
const naamPastOpPlek = (a, b, pad) =>
  zelfdeNaamTekst(a, b) || (pad.length === 1 && ouderVanZelfdeKind(a, b));

/**
 * Generatie 1: `zelfdeNaam`, of de achternaam gelijk volgens die regel en de voornaam gelijk op klank
 * (Arie / Arij) of een initiaal die erbij past (G. / Gerrit).
 * @param {string} a
 * @param {string} b
 */
function ouderVanZelfdeKind(a, b) {
  const [x, y] = [a, b].map((n) => splits(n.toLowerCase()));
  if (zelfdeNaam(a.toLowerCase(), b.toLowerCase())) return true;
  if (!x.voornaam || !y.voornaam || !zelfdeNaam(`x ${x.achternaam}`, `x ${y.achternaam}`))
    return false;
  const [v, w] = [x.voornaam.split(' ')[0], y.voornaam.split(' ')[0]];
  /** @param {string} i @param {string} n */
  const initiaal = (i, n) => /^\p{L}\.?$/u.test(i) && n[0] === i[0];
  return zelfdeKlank(v, w) || initiaal(v, w) || initiaal(w, v);
}

// ---------------------------------------------------------------------------------------------
// Niveaus en startpunten
// ---------------------------------------------------------------------------------------------

/** @type {Record<string, number>} */
export const NIVEAU_RANG = {
  waarschijnlijk: 1,
  'zeer waarschijnlijk': 2,
  'volgens-akte': 3,
};
/** @param {string} n */
export const rang = (n) => NIVEAU_RANG[n] ?? 0;

/**
 * Hoogstens zoveel startpunten per persoon krijgen een familie-vraag (elke vraag laat PeckArchive een
 * stamboom lopen): eerst het kind/de dopeling (geboorte, doop), dan bruid/bruidegom (huwelijk), dan de
 * overledene, de rest valt af. De "ook in"-batch blijft voor alle startpunten.
 */
export const FAMILIE_MAX_PER_PERSOON = 4;
/** @param {{ inAkte?: { soort?: string } | null }} s */
export const familieRang = (s) => {
  const soort = s.inAkte?.soort;
  if (soort === 'birth' || soort === 'baptism') return 0;
  if (soort === 'marriage') return 1;
  if (soort === 'death' || soort === 'burial') return 2;
  return 3;
};
/**
 * De startpunten die een familievraag krijgen. Array.prototype.sort is stabiel: binnen een groep blijft
 * de volgorde van de server.
 * @template {{ inAkte?: { soort?: string } | null }} S
 * @param {S[]} startpunten
 * @returns {S[]}
 */
export const familieStartpunten = (startpunten) =>
  [...startpunten]
    .sort((a, b) => familieRang(a) - familieRang(b))
    .slice(0, FAMILIE_MAX_PER_PERSOON);

// ---------------------------------------------------------------------------------------------
// Voorouders samenvoegen
// ---------------------------------------------------------------------------------------------

/**
 * @typedef {{ soort: string, akte_id: string, pid: string }} Stap
 * @typedef {{ generatie: number, rol: 'vader' | 'moeder', akte_id: string, pid: string,
 *   naam: { voornaam: string, achternaam: string }, naam_weergave?: NaamWeergave | null,
 *   niveau: string, pad: Stap[], afgewezen?: boolean }} Voorouder
 */

/**
 * De voorouders van alle familieantwoorden van één persoon als groepen: per groep de leden (dezelfde
 * persoon volgens de akten) en of die plek tegenspraak heeft.
 * @template {Voorouder} V
 * @param {V[]} alle
 */
export function voorouderGroepen(alle) {
  // Rol van elke genoemde ouder: de tussenstappen van een pad zijn zelf ook voorouders in de lijst.
  const rolVan = new Map(alle.map((v) => [stapSleutel(v), v.rol]));
  /** @param {V} v @returns {string | null} */
  const padVan = (v) => {
    const stappen = v.pad.filter((s) => s.soort === 'volgens-akte');
    if (stappen.length !== v.generatie) return null;
    let pad = '';
    for (const s of stappen) {
      const r = rolVan.get(stapSleutel(s));
      if (!r) return null;
      pad += r === 'moeder' ? 'm' : 'v';
    }
    return pad;
  };

  // De eigen stap van een voorouder: de stap direct na zijn eigen stap in een langer pad dat met het zijne
  // begint (zijn eigen huwelijk, of zijn eigen geboorteakte). Per pad is zijn akte_id|pid die van zijn KIND,
  // dus de eerste gedeelde voorouder heeft per pad een andere sleutel; deze stap is voor beide paden gelijk.
  /** @param {V} v @returns {string | null} */
  const eigenStap = (v) => {
    const n = v.pad.length;
    const langer = alle.find(
      (w) => w.pad.length > n && v.pad.every((st, i) => stapSleutel(w.pad[i]) === stapSleutel(st))
    );
    const st = langer?.pad[n];
    if (!st) return null;
    // Volgens-akte: zijn ouders staan in dezelfde akte (zijn eigen), alleen de akte telt, niet welke ouder.
    return st.soort === 'volgens-akte' ? `a:${akteSleutel(st.akte_id)}` : `s:${stapSleutel(st)}`;
  };
  /** @type {Map<string, string>} */
  const eigen = new Map();
  for (const v of alle) {
    const e = eigenStap(v);
    if (e && !eigen.has(stapSleutel(v))) eigen.set(stapSleutel(v), e);
  }

  // Stap 1: dezelfde voorouder (akte_id|pid) via meer paden is één lid met meer Sosa-nummers
  // (kwartierverlies).
  /**
   * @type {Map<string, { v: V, pads: Set<string>, padOnleesbaar: boolean, akten: Set<string>,
   *   niveau: V['niveau'], padNiveaus: string[], afgewezen: boolean, stappen: Set<string> }>}
   * `stappen`: alle (akte, persoon)-stappen van al zijn paden: wie hem als schakel heeft, loopt door hem heen.
   */
  const perSleutel = new Map();
  for (const v of alle) {
    const pad = padVan(v);
    const niveaus = v.pad
      .filter((s) => s.soort !== 'volgens-akte' && /** @type {{ niveau?: string }} */ (s).niveau)
      .map((s) => /** @type {string} */ (/** @type {{ niveau?: string }} */ (s).niveau));
    const akten = v.pad.map((s) => akteSleutel(s.akte_id));
    const s = stapSleutel(v);
    const bestaand = perSleutel.get(s);
    if (!bestaand) {
      perSleutel.set(s, {
        v,
        pads: new Set(pad ? [pad] : []),
        padOnleesbaar: pad === null,
        akten: new Set(akten),
        niveau: v.niveau,
        padNiveaus: niveaus,
        afgewezen: !!v.afgewezen,
        stappen: new Set(v.pad.map(stapSleutel)),
      });
      continue;
    }
    v.pad.forEach((st) => bestaand.stappen.add(stapSleutel(st)));
    if (pad) bestaand.pads.add(pad);
    else bestaand.padOnleesbaar = true;
    akten.forEach((a) => bestaand.akten.add(a));
    if (rang(v.niveau) < rang(bestaand.niveau)) {
      bestaand.niveau = v.niveau;
      bestaand.padNiveaus = niveaus;
    }
    bestaand.afgewezen ||= !!v.afgewezen;
  }

  // Stap 2: per plek (zelfde pad) worden namen die bij elkaar passen één voorstel. Passen ze niet bij
  // elkaar, dan blijven het aparte regels, allemaal als tegenspraak: Pecktree kiest niet. Een
  // afgewezen voorouder doet niet mee: die is al weggezet en zegt niets meer over deze plek.
  const ouder = new Map([...perSleutel.keys()].map((k) => [k, k]));
  /** @param {string} k @returns {string} */
  const wortel = (k) => (ouder.get(k) === k ? k : wortel(/** @type {string} */ (ouder.get(k))));
  /** @param {string} k */
  const lid = (k) =>
    /** @type {NonNullable<ReturnType<typeof perSleutel.get>>} */ (perSleutel.get(k));
  // Dezelfde eigen stap = dezelfde man, ook als de akte van zijn kind per pad anders is (kwartierverlies,
  // bv. een neef-nicht-huwelijk). Zonder langer pad is dat niet te bewijzen: dan blijven het aparte regels.
  /** @type {Map<string, string[]>} */
  const perEigen = new Map();
  for (const [k, l] of perSleutel) {
    const e = eigen.get(k);
    if (e && !l.afgewezen) perEigen.set(e, [...(perEigen.get(e) ?? []), k]);
  }
  for (const ks of perEigen.values())
    for (const k of ks.slice(1)) ouder.set(wortel(k), wortel(ks[0]));
  /** @type {Map<string, string[]>} */
  const opPlek = new Map();
  for (const [k, l] of perSleutel) {
    if (l.afgewezen || geenNaam(toonNaam(l.v))) continue;
    for (const pad of l.pads) opPlek.set(pad, [...(opPlek.get(pad) ?? []), k]);
  }
  for (const [pad, sleutels] of opPlek) {
    for (let i = 0; i < sleutels.length; i++) {
      for (let j = i + 1; j < sleutels.length; j++) {
        if (naamPastOpPlek(toonNaam(lid(sleutels[i]).v), toonNaam(lid(sleutels[j]).v), pad)) {
          ouder.set(wortel(sleutels[j]), wortel(sleutels[i]));
        }
      }
    }
  }
  const tegenspraak = new Set();
  for (const sleutels of opPlek.values()) {
    const groepen = new Set(sleutels.map(wortel));
    if (groepen.size > 1) groepen.forEach((w) => tegenspraak.add(w));
  }
  /** @type {Map<string, string[]>} */
  const samen = new Map();
  for (const [k, l] of perSleutel) {
    // Een ouder zonder naam (N.N.) wordt geen voorstel; zijn akte telt nergens tegen.
    if (geenNaam(toonNaam(l.v))) continue;
    samen.set(wortel(k), [...(samen.get(wortel(k)) ?? []), k]);
  }
  // Samenvoegen mag niet via een ketting (A~B, B~C maar A niet ~ C): elk paar in de groep moet passen,
  // anders wordt er niets samengevoegd en is elke naam een eigen regel met tegenspraak.
  /** @param {string} a @param {string} b */
  const passen = (a, b) =>
    (eigen.has(a) && eigen.get(a) === eigen.get(b)) ||
    [...lid(a).pads]
      .filter((p) => lid(b).pads.has(p))
      .some((p) => naamPastOpPlek(toonNaam(lid(a).v), toonNaam(lid(b).v), p)) ||
    zelfdeNaamTekst(toonNaam(lid(a).v), toonNaam(lid(b).v));
  /** @type {Array<{ sleutels: string[], tegenspraak: boolean }>} */
  const groepen = [];
  for (const [w, sleutels] of samen) {
    if (sleutels.every((a, i) => sleutels.slice(i + 1).every((b) => passen(a, b)))) {
      groepen.push({ sleutels, tegenspraak: tegenspraak.has(w) });
    } else {
      sleutels.forEach((k) => groepen.push({ sleutels: [k], tegenspraak: true }));
    }
  }
  return { perSleutel, groepen };
}

/**
 * De voorstellen van generatie 1 zoals de stamboom ze toont waar die ouder nog ontbreekt (vondstenModel
 * `voorouderRegels` + boomLaag): per plek ('v' vader, 'm' moeder) het niveau van het voorstel (het
 * zwakste van de samengevoegde akten) en of de akten elkaar tegenspreken. Een afgewezen voorouder telt
 * niet mee, en een groep met een onleesbaar pad ook niet (de laag toont hem niet).
 * @param {Voorouder[]} alle
 * @returns {Array<{ pad: 'v' | 'm', niveau: string, tegenspraak: boolean }>}
 */
export function eersteGeneratiePlekken(alle) {
  const { perSleutel, groepen } = voorouderGroepen(alle);
  /** @type {Map<string, Array<{ niveau: string, tegenspraak: boolean }>>} */
  const opPlek = new Map();
  for (const { sleutels, tegenspraak } of groepen) {
    const leden = sleutels.map(
      (k) => /** @type {NonNullable<ReturnType<typeof perSleutel.get>>} */ (perSleutel.get(k))
    );
    const pads = new Set(leden.flatMap((l) => [...l.pads]));
    if (!pads.size || leden.some((l) => l.padOnleesbaar || l.afgewezen)) continue;
    const niveau = leden.reduce((z, l) => (rang(l.niveau) < rang(z.niveau) ? l : z)).niveau;
    for (const pad of pads) {
      if (pad.length === 1) opPlek.set(pad, [...(opPlek.get(pad) ?? []), { niveau, tegenspraak }]);
    }
  }
  return [...opPlek].map(([pad, regels]) => ({
    pad: /** @type {'v' | 'm'} */ (pad),
    niveau: regels[0].niveau,
    // Meer regels op één plek, of een regel die zelf tegenspraak meldt.
    tegenspraak: regels.length > 1 || regels[0].tegenspraak,
  }));
}
